import roles from 'consts/roles';
import { IsJsonString, checkPlivoPowerPack, getCountryName } from 'helpers';
import { useSelector } from 'react-redux';
import { cloneDeep, get, isArray, isEmpty, set } from 'lodash';
import { useMemo } from 'react';
import ColumnMonitoringModel, { enumColumn } from 'models/column-monitoring.model';
import ColumnContactModel from 'models/column-contact.model';
import {
  ContactFieldsShouldHided,
  FilterConditionEnumFallback,
  getTimezoneText,
  optionsPayViaCredit,
  PayViaEnum,
} from 'consts/data';
import CacheService from 'services/CacheService';

const parseRoutes = (routes) => {
  if (isArray(routes)) {
    const nextRoutes = cloneDeep(routes);
    nextRoutes.forEach((route) => {
      if (isArray(route?.details)) {
        //* Sort details
        route.details = route.details
          .filter((el) => !!el?.setting?.active)
          .sort((a, b) => {
            const countryNameA = getCountryName(a.country);
            const countryNameB = getCountryName(b.country);
            return countryNameA.localeCompare(countryNameB);
          });
      }
    });

    //* Sort route
    return nextRoutes.sort((a, b) => {
      return a.name.localeCompare(b.name);
    });
  }

  return [];
};

const middleware = (nextUser) => {
  //* Disable sortable on statistic
  if (!isEmpty(nextUser.layoutMonitoring)) {
    nextUser.layoutMonitoring.forEach((el) => {
      if (el.value === enumColumn.statistic && !isEmpty(el.subColumn)) {
        el.subColumn.forEach((s) => {
          s.isSortable = false;
        });
      }
    });
  }
};

export default () => {
  const isLogin = useSelector((state) => state.authReducer.isLogin);
  const user = useSelector((state) => state.userServletReducer.data) || {};

  const userMemo = useMemo(() => {
    if (isLogin) {
      const nextUser = cloneDeep(user);

      if (!isEmpty(nextUser)) {
        const campaignMonitoringLayout = nextUser?.settings?.campaignMonitoringLayout;

        const contactGroupLayout = nextUser?.settings?.contactGroupLayout;

        nextUser.isPowerPack = checkPlivoPowerPack(nextUser?.phoneType);
        nextUser.isSuperAdmin = (nextUser?.roles || []).includes(roles.superAdmin);
        nextUser.isAgency = (nextUser?.roles || []).includes(roles.managementAgency);
        nextUser.routesInfo = parseRoutes(nextUser?.routes);
        nextUser.isLogged = true;
        nextUser.userId = nextUser.id;
        nextUser.timezone = getTimezoneText(nextUser?.timeName);
        // Fall back to the FilterCondition list when the deployed backend omits
        // enumSetting (drift) so the "Select condition" dropdown isn't empty (TEX-239).
        const enumSetting = !isEmpty(nextUser.enumSetting)
          ? nextUser.enumSetting
          : FilterConditionEnumFallback;
        nextUser.enumSetting = enumSetting;
        nextUser.enumSettingNormalized = Object.entries(enumSetting).map(([value, label]) => ({
          label,
          value,
        }));

        nextUser.layoutContact =
          contactGroupLayout && IsJsonString(contactGroupLayout)
            ? isArray(JSON.parse(contactGroupLayout))
              ? JSON.parse(contactGroupLayout)
                  .filter((el) => !!el)
                  .filter(
                    (el) =>
                      !ContactFieldsShouldHided.slice(1, ContactFieldsShouldHided.length).includes(
                        el.value
                      )
                  )
              : ColumnContactModel.getDefaultColumns()
            : ColumnContactModel.getDefaultColumns();

        nextUser.monitoringV1Layout =
          (campaignMonitoringLayout &&
            IsJsonString(campaignMonitoringLayout) &&
            JSON.parse(campaignMonitoringLayout)?.v1) ||
          new ColumnMonitoringModel().columns;
        nextUser.monitoringV2Layout =
          (campaignMonitoringLayout &&
            IsJsonString(campaignMonitoringLayout) &&
            JSON.parse(campaignMonitoringLayout)?.v2) ||
          [];

        set(
          nextUser,
          'settings.clickFilterSetting',
          JSON.parse(get(nextUser, 'settings.clickFilterSetting') || '{}')
        );

        set(
          nextUser,
          'settings.shortlinkTagSetting',
          JSON.parse(get(nextUser, 'settings.shortlinkTagSetting') || null) || {}
        );
      }

      let payMethodsAvailable = optionsPayViaCredit;
      if (!nextUser?.settings?.enableCreditCard) {
        payMethodsAvailable = payMethodsAvailable.filter(
          (el) => el.value !== PayViaEnum.CreditCard
        );
      }
      set(nextUser, 'payMethodsAvailable', payMethodsAvailable);

      middleware(nextUser);
      CacheService.set('userInfo', nextUser);
      return nextUser;
    }

    return {};
  }, [isLogin, user]);

  return userMemo;
};
